import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { PsychologistProfile } from "core/types";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Icon, TextField } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { useDemo } from "contexts/DemoContext";
import { useApplicationMarket } from "../../../core/selectors/marketSelectors";
import { HeaderRow, InfoButton } from "../styled";
import {
  getDescriptions,
  getUpdatedDescriptions,
} from "./descriptionFormatting";
import { DescriptionInfoModal } from "./descriptionInfoModal";
import { CreateDescriptionMessages } from "./type";

type Props = {
  psychologist: Pick<PsychologistProfile, "descriptions">;
  onChangeDescriptions: (descriptions: CreateDescriptionMessages) => void;
  psychologistValidation: string[];
};

const Descriptions = forwardRef<HTMLDivElement, Props>(
  ({ psychologist, onChangeDescriptions, psychologistValidation }, ref) => {
    const { t } = useTranslation();
    const activeCountry =
      useApplicationMarket() as RouterOutput["application"]["markets"][number];
    const { defaultLanguageId, languages } = activeCountry;
    const demo = useDemo();
    const [infoModalOpen, setInfoModalOpen] = useState(false);

    const descriptions = getDescriptions({
      descriptions: psychologist?.descriptions,
      activeMarket: activeCountry,
    });

    return (
      <div ref={ref}>
        <DescriptionInfoModal
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
        />

        <HeaderRow>
          <Typography variant="h3" style={{ margin: "10px 0px" }} insensitive>
            {t("PRESENTATION")}
          </Typography>
          <InfoButton onClick={() => setInfoModalOpen(true)}>
            <Icon.Info />
          </InfoButton>
        </HeaderRow>
        <Typography
          variant="body-small"
          style={{ margin: "10px 0px" }}
          insensitive
        >
          {t("DESCRIPTION_INFO")}
        </Typography>
        <DescriptionsContainer>
          {descriptions?.map((description, index) => {
            const isDefaultForCountry =
              description.countryLanguage.languageId === defaultLanguageId;

            return (
              <TextField
                key={`description_lang_${index}`}
                hideSensitiveData={false}
                error={psychologistValidation.indexOf("descriptions") !== -1}
                errorMessage={t("REQUIRED_FIELDS")}
                label={`${t(
                  languages
                    ?.find(
                      (language) =>
                        language.languageId ===
                        description.countryLanguage.languageId,
                    )
                    ?.codeISO3.toUpperCase() || "",
                )}${isDefaultForCountry ? "*" : ""}`}
                placeholder={
                  isDefaultForCountry
                    ? t("ENTER_DESCRIPTION")
                    : t("ENTER_TRANSLATION")
                }
                multiline
                value={description.description}
                sensitiveField={demo}
                onChange={(e) => {
                  const updatedDescriptions = getUpdatedDescriptions(
                    descriptions,
                    description.countryLanguage.countryId,
                    description.countryLanguage.languageId,
                    e.target.value,
                  );
                  onChangeDescriptions(updatedDescriptions);
                }}
                maxlength={800}
              />
            );
          })}
        </DescriptionsContainer>
      </div>
    );
  },
);

const DescriptionsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

Descriptions.displayName = "Descriptions";

export default Descriptions;
